import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, KeyRound, Trash2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDateTime } from '@/lib/dates';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, Spinner } from '@/features/common/ui';

interface ApiKeyRow {
  id: string;
  name: string;
  keyPrefix: string;
  creatorName: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  isActive: boolean;
}

/**
 * API keys, and the one place the plaintext is ever shown.
 *
 * These are what an MCP client connects with, so the copy here says what that
 * connection can actually do rather than leaving it to be discovered: read
 * everything the key owner's role can read, and raise, answer or resolve a
 * review query. Nothing else.
 */
export function ApiKeysCard() {
  const org = useActiveOrg();
  const perms = usePermissions();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [justCreated, setJustCreated] = useState<{ key: string; name: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const keys = useQuery({
    queryKey: ['api-keys', org?.id],
    queryFn: () => api.get<{ items: ApiKeyRow[] }>('/api-keys'),
    enabled: !!org?.id && perms.can('apiKey', 'view'),
  });

  const create = useMutation({
    mutationFn: () => api.post<{ key: string; name: string }>('/api-keys', { name: name.trim() }),
    onSuccess: (created) => {
      setJustCreated(created);
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['api-keys', org?.id] });
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/api-keys/${id}`),
    // A block body, not a concise one. `no-void` is configured with
    // `allowAsStatement`, so `void somePromise()` is fine as a statement - which
    // is what the identical call in `create.onSuccess` above is - but not as an
    // arrow's returned expression. This was the only place in the web app doing
    // the latter, out of some thirty `void` uses.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['api-keys', org?.id] });
    },
  });

  if (!perms.can('apiKey', 'view')) return null;
  const canManage = perms.can('apiKey', 'manage');
  const error = create.error instanceof ApiError ? create.error : undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle>API keys and AI agents</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          A key lets an AI agent, or anything else outside the browser, connect to this book at{' '}
          <code className="rounded bg-muted px-1 py-0.5 text-xs">/v1/mcp</code>. The connection can
          read what you can read and can raise, answer or resolve a review query. It cannot issue an
          invoice, void one, record a payment or change any figure.
        </p>
        {/*
          Somebody looking for "how do I disconnect Claude" looks here first,
          because this card is where Claude was mentioned before connections
          existed. One sentence saves them a support message.
        */}
        <p className="text-sm text-muted-foreground">
          Connecting an AI agent (Claude, ChatGPT or any other MCP client) from its own settings needs no key at all — see{' '}
          <span className="font-medium text-foreground">Connected assistants</span> above, which is
          also where you disconnect one.
        </p>

        {justCreated ? (
          <div className="space-y-2 rounded-md border border-warning/40 bg-warning/5 p-3">
            <p className="text-sm font-medium">Copy this now — it is never shown again.</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 overflow-x-auto rounded bg-background px-2 py-1 font-mono text-xs">
                {justCreated.key}
              </code>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  void navigator.clipboard?.writeText(justCreated.key);
                  setCopied(true);
                }}
              >
                <Copy className="size-4" aria-hidden />
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <Button size="sm" variant="ghost" onClick={() => { setJustCreated(null); setCopied(false); }}>
              Done
            </Button>
          </div>
        ) : null}

        {canManage ? (
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (name.trim()) create.mutate();
            }}
          >
            <Input
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="My agent on the laptop"
              error={error?.fieldError('name')}
              className="min-w-[220px]"
            />
            <Button type="submit" size="sm" disabled={!name.trim() || create.isPending}>
              <KeyRound className="size-4" aria-hidden />
              Create key
            </Button>
          </form>
        ) : null}

        {keys.isLoading ? (
          <Spinner />
        ) : keys.data?.items.length ? (
          <ul className="space-y-2">
            {keys.data.items.map((key) => (
              <li key={key.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{key.name}</p>
                  <p className="font-mono text-xs text-muted-foreground">{key.keyPrefix}…</p>
                  <p className="text-xs text-muted-foreground">
                    Created by {key.creatorName ?? 'someone'} ·{' '}
                    {key.lastUsedAt ? `last used ${formatDateTime(key.lastUsedAt)}` : 'never used'}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge variant={key.isActive ? 'success' : 'neutral'}>
                    {key.isActive ? 'Active' : 'Revoked'}
                  </Badge>
                  {key.isActive && canManage ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={revoke.isPending}
                      onClick={() => {
                        if (window.confirm(`Revoke "${key.name}"? Anything using it stops working immediately.`)) {
                          revoke.mutate(key.id);
                        }
                      }}
                    >
                      <Trash2 className="size-4" aria-hidden />
                      Revoke
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No keys yet.</p>
        )}
      </CardContent>
    </Card>
  );
}
